<template>
  <b-card no-body>
    <b-card-header class="d-flex align-items-center justify-content-between">
      <div>
        <h4 class="mb-0">
          Standort Leistungsvergleich
        </h4>
        <small class="text-muted">Umsatz und Auslastung nach Standorten</small>
      </div>
      <div class="d-flex">
        <b-dropdown
          text="Zeitraum"
          variant="outline-primary"
          size="sm"
          no-caret
          class="mr-2"
        >
          <b-dropdown-item
            v-for="period in timePeriods"
            :key="period.value"
            :active="selectedPeriod === period.value"
            @click="updatePeriod(period.value)"
          >
            {{ period.label }}
          </b-dropdown-item>
        </b-dropdown>
        <b-dropdown
          variant="outline-secondary"
          size="sm"
          no-caret
        >
          <template #button-content>
            <feather-icon
              icon="DownloadIcon"
              size="16"
            />
          </template>
          <b-dropdown-item @click="exportChartData('csv')">
            <feather-icon
              icon="FileTextIcon"
              size="14"
              class="mr-1"
            />
            Als CSV exportieren
          </b-dropdown-item>
          <b-dropdown-item @click="exportChartData('png')">
            <feather-icon
              icon="ImageIcon"
              size="14"
              class="mr-1"
            />
            Als PNG exportieren
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </b-card-header>
    
    <b-card-body class="px-3 py-3">
      <!-- Location Performance Metrics -->
      <b-row class="mb-3">
        <b-col
          v-for="location in locationStats"
          :key="location.name"
          cols="12"
          md="6"
          lg="4"
          class="mb-3"
        >
          <div class="location-card p-3 border rounded">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">
                {{ location.name }}
              </h6>
              <b-badge
                :variant="getPerformanceBadgeVariant(location.performance)"
                pill
              >
                {{ Math.round(location.performance) }}%
              </b-badge>
            </div>
            
            <div class="location-metrics">
              <div class="metric-item d-flex justify-content-between">
                <span class="text-muted">Umsatz:</span>
                <strong>{{ formatCurrency(location.revenue) }}</strong>
              </div>
              <div class="metric-item d-flex justify-content-between">
                <span class="text-muted">Auslastung:</span>
                <strong>{{ Math.round(location.occupancy) }}%</strong>
              </div>
              <div class="metric-item d-flex justify-content-between">
                <span class="text-muted">Lager gesamt:</span>
                <strong>{{ location.totalStorages }}</strong>
              </div>
              <div class="metric-item d-flex justify-content-between">
                <span class="text-muted">Aktive Mieten:</span>
                <strong>{{ location.activeRentals }}</strong>
              </div>
            </div>
            
            <!-- Location performance bar -->
            <div class="performance-bar mt-2">
              <div
                class="progress"
                style="height: 6px;"
              >
                <div
                  class="progress-bar"
                  :class="`bg-${getPerformanceBadgeVariant(location.performance)}`"
                  :style="{ width: Math.min(location.performance, 100) + '%' }"
                />
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Revenue Comparison Chart -->
      <div class="chart-container">
        <h6 class="mb-3">
          Umsatzvergleich nach Standorten
        </h6>
        <apexchart
          ref="chart"
          type="bar"
          height="300"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BRow, BCol, BBadge, BDropdown, BDropdownItem } from 'bootstrap-vue'
import ApexCharts from 'vue-apexcharts'
import { $themeColors } from '@themeConfig'
import businessMetrics from '@/helper/businessMetrics'

export default {
  name: 'LocationPerformanceChart',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BBadge,
    BDropdown,
    BDropdownItem,
    apexchart: ApexCharts,
  },
  setup() {
    const { getLocationPerformanceData } = businessMetrics()
    return {
      getLocationPerformanceData,
    }
  },
  data() {
    return {
      selectedPeriod: 'current',
      timePeriods: [
        { value: 'current', label: 'Aktueller Monat' },
        { value: 'last3months', label: 'Letzte 3 Monate' },
        { value: 'last6months', label: 'Letzte 6 Monate' },
        { value: 'last12months', label: 'Letztes Jahr' },
      ],
      locationStats: [],
      chartSeries: [],
      chartOptions: {
        chart: {
          type: 'bar',
          height: 300,
          toolbar: { 
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
          fontFamily: 'inherit',
        },
        colors: [
          $themeColors.primary,
          $themeColors.success,
          $themeColors.warning,
          $themeColors.info,
          $themeColors.danger,
        ],
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '60%',
            distributed: true,
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        grid: {
          borderColor: '#e4e6e8',
          strokeDashArray: 3,
          padding: {
            top: -20,
            bottom: 5,
            left: 20,
          },
        },
        xaxis: {
          categories: [],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: {
              colors: '#6c757d',
              fontSize: '12px',
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: '#6c757d',
              fontSize: '12px',
            },
            formatter: val => this.formatCurrency(val),
          },
        },
        tooltip: {
          y: {
            formatter: val => this.formatCurrency(val),
          },
        },
      },
    }
  },
  mounted() {
    this.loadLocationData()
  },
  methods: {
    exportChartData(format) {
      if (format === 'csv') {
        this.exportToCSV()
      } else if (format === 'png') {
        this.exportToPNG()
      }
    },
    exportToCSV() {
      const headers = ['Standort', 'Umsatz (€)', 'Auslastung (%)', 'Lager gesamt', 'Aktive Mieten', 'Performance (%)']
      const rows = this.locationStats.map(location => [
        location.name,
        location.revenue,
        Math.round(location.occupancy),
        location.totalStorages,
        location.activeRentals,
        Math.round(location.performance),
      ])
      
      let csvContent = `${headers.join(',')}\n`
      rows.forEach(row => {
        csvContent += `${row.join(',')}\n`
      })
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'standort-leistungsvergleich.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    exportToPNG() {
      this.$refs.chart.dataURI().then(uri => {
        const link = document.createElement('a')
        link.href = uri
        link.download = 'standort-leistungsvergleich.png'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
    },
    loadLocationData() {
      const data = this.getLocationPerformanceData(this.selectedPeriod)
      this.locationStats = data.locations
      
      // Prepare chart data
      this.chartSeries = [
        {
          name: 'Umsatz',
          data: data.locations.map(location => location.revenue),
        },
      ]
      
      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: data.locations.map(location => location.name),
        },
      }
    },
    updatePeriod(period) {
      this.selectedPeriod = period
      this.loadLocationData()
    },
    getPerformanceBadgeVariant(performance) {
      if (performance >= 80) return 'success'
      if (performance >= 60) return 'warning'
      return 'danger'
    },
    formatCurrency(amount) {
      return `${amount.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`
    },
  },
}
</script>

<style scoped>
.location-card {
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.location-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.metric-item {
  margin-bottom: 4px;
  font-size: 0.875rem;
}

.performance-bar {
  margin-top: 8px;
}

.chart-container {
  margin-top: 2rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .metric-item {
    font-size: 0.8rem;
  }
}
</style>
